import React, { useCallback, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { palette, radius, space, status, typography } from '../../../style-utils/theme';
import { AdminAlert, getAlerts } from '../../../api/admin-client';
import { Btn } from './admin.styled';
import { whenExact } from './admin-table';

/**
 * THE CONSOLE'S ALARM, at the top of EVERY page.
 *
 * Production, 2026-09-24: SendGrid refused every send for a day ("Maximum
 * credits exceeded") — invitations, resets, verification, daily reports — and
 * the one line an operator could find said `sendgrid: configured`. An alarm
 * that lives on one screen of thirty is a log line with better typography, so
 * this sits above every page's title and cannot be dismissed while the
 * condition holds: it goes away when the server stops reporting it.
 *
 * Asked once a minute. The server answers from cached probes, so the poll
 * costs nothing at any vendor; "Re-check now" forces fresh ones (the server
 * allows one every 30 seconds).
 *
 * TWO LEVELS, and they must not look the same. `critical` is something that
 * worked and has stopped — email not being delivered, address search out of
 * credits, the map tiles not answering. `warning` is true and worth saying but
 * is not an outage — a service that is switched off, or close to its daily
 * limit. If a standing warning were as red as an outage, the outage would be
 * the one nobody notices.
 */

const POLL_MS = 60 * 1000;

type Level = AdminAlert['level'];

// Block-body readers, kept out of the template so a long member expression is
// never wrapped inside it.
const toneOf = (level: Level) => {
  return level === 'warning' ? status.warning : status.alert;
};
const edgeOf = (level: Level) => {
  return level === 'warning' ? status.warning.dot : palette.alert;
};

const Banner = styled.section<{ $level: Level }>`
  display: grid;
  gap: ${space(0.5)};
  margin-bottom: ${space(2)};
  padding: ${space(2)} ${space(2)};
  border: 1px solid ${p => edgeOf(p.$level)};
  border-left-width: 6px;
  border-radius: ${radius.sm};
  background: ${p => toneOf(p.$level).bg};
  color: ${p => toneOf(p.$level).fg};
`;

const Title = styled.h2`
  margin: 0;
  font-size: ${typography.size.base}px;
  font-weight: ${typography.weight.bold};
  color: inherit;
`;

const Body = styled.p`
  margin: 0;
  font-size: ${typography.size.sm}px;
  line-height: 1.5;
`;

const Foot = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${space(1)};
  font-size: ${typography.size.xs}px;
`;

// The reader's own clock, like the rest of the console (see admin-table).
const when = (iso?: string | null) => {
  const text = whenExact(iso);

  return text === '—' ? null : text;
};

export const AdminAlerts: React.FC = () => {
  const [alerts, setAlerts] = useState<AdminAlert[]>([]);
  const [checking, setChecking] = useState(false);

  const load = useCallback(async (recheck = false) => {
    try {
      const r = await getAlerts(recheck);
      setAlerts(Array.isArray(r.alerts) ? r.alerts : []);
    } catch {
      // The alarm must never become the outage: a failed poll keeps what was
      // last known rather than blanking a real alert.
    }
  }, []);

  useEffect(() => {
    load();
    const t = window.setInterval(() => load(), POLL_MS);

    return () => window.clearInterval(t);
  }, [load]);

  if (!alerts.length) return null;

  return (
    <>
      {alerts.map(a => (
        <Banner
          key={a.key}
          $level={a.level}
          // An outage interrupts a screen reader; a standing warning waits its turn.
          role={a.level === 'warning' ? 'status' : 'alert'}
          aria-live={a.level === 'warning' ? 'polite' : 'assertive'}
          data-admin-alert={a.key}
          data-admin-alert-level={a.level}
        >
          <Title>{a.title}</Title>
          <Body>{a.detail}</Body>
          <Body>
            <strong>What to do:</strong> {a.action}
          </Body>
          <Foot>
            {when(a.since) ? (
              <span>
                {a.level === 'warning' ? 'Since' : 'Failing since'} {when(a.since)}
              </span>
            ) : null}
            {when(a.checked_at) ? (
              <span>
                {when(a.since) ? '· ' : ''}last checked {when(a.checked_at)}
              </span>
            ) : null}
            <Btn
              type="button"
              disabled={checking}
              onClick={async () => {
                setChecking(true);
                await load(true);
                setChecking(false);
              }}
            >
              {checking ? 'Checking…' : 'Re-check now'}
            </Btn>
          </Foot>
        </Banner>
      ))}
    </>
  );
};
